import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import Chip from "../../components/atoms/Chip";

/**
 * Shared hub layout.
 * Each hub is a focused landing surface — one eyebrow, one promise, a
 * card grid below. Outcome-oriented copy (verbs and benefits), not
 * system labels.
 */
export default function HubLayout({ eyebrow, headline, italic, subhead, cards }) {
  return (
    <div className="page-enter" style={{ paddingBottom: 64, display: "flex", flexDirection: "column", gap: 32 }}>
      <header style={{ maxWidth: "62ch" }}>
        <Chip tone="gold">{eyebrow}</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(44px, 7vw, 68px)", lineHeight: 1.02, letterSpacing: "-0.025em", margin: "16px 0 16px", fontWeight: 400 }}>
          {headline} {italic && <em style={{ color: "var(--gold-500)" }}>{italic}</em>}
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 18, lineHeight: 1.55, margin: 0 }}>
          {subhead}
        </p>
      </header>

      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {cards.map((c, i) => <HubCard key={i} card={c} />)}
      </div>
    </div>
  );
}

function HubCard({ card }) {
  const { title, lede, to, Icon, status = "live", accent = "var(--gold-500)" } = card;
  const isLive = status === "live";

  const inner = (
    <article style={{
      position: "relative",
      padding: "28px 24px 24px",
      background: isLive ? "#fff" : "var(--paper-deep)",
      border: `1px solid ${isLive ? "var(--border-1)" : "var(--border-1)"}`,
      borderTop: `3px solid ${isLive ? accent : "var(--ink-200)"}`,
      borderRadius: "var(--radius-lg)",
      transition: "transform 200ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms cubic-bezier(.2,.8,.2,1)",
      cursor: isLive ? "pointer" : "default",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      opacity: isLive ? 1 : 0.7,
    }}
    onMouseEnter={(e) => {
      if (!isLive) return;
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "var(--shadow-card)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "none";
      e.currentTarget.style.boxShadow = "none";
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{
          width: 44, height: 44,
          borderRadius: "var(--radius-md)",
          background: isLive ? `${accent}18` : "var(--ink-100)",
          color: isLive ? accent : "var(--ink-400)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          {Icon && <Icon size={22} strokeWidth={1.6} />}
        </div>
        {!isLive && (
          <span style={{
            fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
            color: "var(--fg-3)",
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 10px", background: "#fff",
            border: "1px solid var(--border-2)", borderRadius: 999,
          }}>
            <Clock size={11} strokeWidth={1.8} /> Soon
          </span>
        )}
      </div>

      <h3 className="font-display" style={{ fontSize: 26, lineHeight: 1.1, fontWeight: 400, margin: "4px 0 0", letterSpacing: "-0.01em", color: "var(--fg-1)" }}>
        {title}
      </h3>

      <p style={{ color: "var(--fg-2)", fontSize: 15, lineHeight: 1.5, margin: 0, flex: 1 }}>
        {lede}
      </p>

      {isLive && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: accent, fontSize: 13, fontWeight: 600, marginTop: 4 }}>
          Open <ArrowRight size={14} strokeWidth={2} />
        </div>
      )}
    </article>
  );

  if (!isLive) return inner;
  return (
    <Link to={to} style={{ textDecoration: "none", color: "inherit", display: "block" }}>
      {inner}
    </Link>
  );
}
